import {Component, OnInit} from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  Validators,
  ReactiveFormsModule
} from '@angular/forms';
import {ToastrService} from 'ngx-toastr';
import {User} from '../../../core/entities/user/user.entity';
import {UserService} from '../../../core/services/user/user.service';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {ToggleComponent} from '../../../core/components/toggle/toggle.component';
import {RouterLink} from '@angular/router';
import {UserImageComponent} from '../../../core/components/user-image/user-image.component';
import imageCompression from 'browser-image-compression';
import {
  PublicProfilePortfolio
} from '../../components/public-profile/public-profile-portfolio/public-profile-portfolio';
import {CitySelector} from '../../../core/components/city-selector/city-selector';
import {TagsSelector} from '../../../core/components/tags-selector/tags-selector';

@Component({
  selector: 'app-public-profile',
  templateUrl: './public-profile.component.html',
  imports: [
    ReactiveFormsModule,
    TranslocoPipe,
    ToggleComponent,
    RouterLink,
    UserImageComponent,
    PublicProfilePortfolio,
    CitySelector,
    TagsSelector,
  ],
  standalone: true,
  styleUrls: ['./public-profile.component.scss']
})
export class PublicProfileComponent implements OnInit {
  public user: User | null = null;
  public profileForm!: FormGroup;
  public selectedFileName: string | null = null;
  public formSubmitted = false;

  constructor(
    private readonly userService: UserService,
    private readonly toastr: ToastrService,
    private readonly fb: FormBuilder,
    private readonly transloco: TranslocoService,
  ) {
    this.initProfileForm();
  }

  ngOnInit(): void {
    this.fetchUserProfile();
  }

  private initProfileForm(): void {
    this.profileForm = this.fb.group({
      online: [false, Validators.required],
      firstname: ['', [Validators.required, Validators.maxLength(50)]],
      lastname: ['', [Validators.required, Validators.maxLength(50)]],
      city: ['', [Validators.required]],
      tags: ['', Validators.required],
      biography: ['', Validators.required],
    });
  }

  private fetchUserProfile(): void {
    this.userService.getUser().subscribe(
      (response) => {
        this.user = response;
        this.profileForm.patchValue(this.user || {});
      },
      () => {
        this.toastr.error(
          this.transloco.translate('public-profile.identity.retrieve-error.content'),
          this.transloco.translate('public-profile.identity.retrieve-error.title')
        );
      }
    );
  }

  public changeOnlineStatus(online: boolean): void {
    // Check first if the values are set
    const {firstname, lastname, city, tags, biography} = this.profileForm.value;
    if (firstname === null || firstname === '' ||
      lastname === null || lastname === '' ||
      city === null || city === '' ||
      tags === null || tags === '' ||
      biography === null || biography === ''
    ) {
      this.showErrorToast('public-profile.online.saved.invalid-profile');
      return;
    }

    this.profileForm.patchValue({online});
    this.userService.updateUser(null, null, online).subscribe(
      () => this.showSuccessToast(online ? 'public-profile.online.saved.online' : 'public-profile.online.saved.offline'),
      () => this.showErrorToast('public-profile.online.saved.error')
    );
  }

  public showSuccessToast(translationKey: string): void {
    this.toastr.success(
      this.transloco.translate(`${translationKey}.content`),
      this.transloco.translate(`${translationKey}.title`)
    );
  }

  public showErrorToast(translationKey: string): void {
    this.toastr.error(
      this.transloco.translate(`${translationKey}.content`),
      this.transloco.translate(`${translationKey}.title`)
    );
  }

  public async onImageSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (file) {
      this.selectedFileName = file.name;

      // Compress the image
      try {
        const options = {
          maxSizeMB: 2,
          maxWidthOrHeight: 800,
          useWebWorker: true
        };
        const compressedFile = await imageCompression(file, options);

        this.userService.updateUserImage(compressedFile).subscribe({
          next: (updatedUser) => {
            this.user = updatedUser;
            this.toastr.success(
              this.transloco.translate('public-profile.identity.image.success.content'),
              this.transloco.translate('public-profile.identity.image.success.title')
            );
          },
          error: () => {
            this.toastr.error(
              this.transloco.translate('public-profile.identity.image.error.content'),
              this.transloco.translate('public-profile.identity.image.error.title')
            );
          }
        });
      } catch (error) {
        this.toastr.error(
          this.transloco.translate('public-profile.identity.image.error.content'),
          this.transloco.translate('public-profile.identity.image.error.title')
        );
        console.error(error);
      }
    }
  }

  public submitForm(): void {
    this.profileForm.markAllAsTouched();
    if (this.profileForm.invalid) {
      this.showErrorToast('public-profile.identity.missing-fields');
      return;
    }

    this.formSubmitted = true;

    const { online, firstname, lastname, city, tags, biography } = this.profileForm.value;

    this.userService.updateUser(firstname, lastname, online, city, tags, biography).subscribe({
      next: () => {
        this.formSubmitted = false;
        this.showSuccessToast('public-profile.identity.saved');
      },
      error: () => {
        this.formSubmitted = false;
        this.showErrorToast('public-profile.identity.saved-error');
      }
    });
  }
}
